import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, Checkbox, FormControlLabel, Accordion,
  AccordionSummary, AccordionDetails, Stack, CircularProgress, TextField, InputAdornment
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Search as SearchIcon } from '@mui/icons-material';
import { roleAPI, rbacAPI } from '../../services/api';
import { useAccessControl, RESOURCES, ACTIONS } from '../../hooks/useAccessControl';
import { getResourceLabel } from '../../hooks/usePermissions';

const AssignPermissionsDialog = ({ open, role, onClose, onSuccess }) => {
  const { can } = useAccessControl();
  const [loading, setLoading] = useState(false);
  const [resources, setResources] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const canAssignPermissions = can(RESOURCES.PERMISSIONS, ACTIONS.ASSIGN);

  useEffect(() => {
    if (open && role) {
      fetchData();
    }
  }, [open, role]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [resourcesRes, permissionsRes, rolePermissionsRes] = await Promise.all([
        rbacAPI.getResources(),
        rbacAPI.getPermissions(),
        roleAPI.getPermissions(role.id)
      ]);

      setResources(resourcesRes.data);
      setPermissions(permissionsRes.data);
      setSelectedPermissions(rolePermissionsRes.data.map(p => Number(p.id)));
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = (permissionId) => {
    const id = Number(permissionId);
    setSelectedPermissions(prev =>
      prev.includes(id)
        ? prev.filter(existing => existing !== id)
        : [...prev, id]
    );
  };

  const handleToggleResource = (resourceId) => {
    const resourcePermissions = permissions
      .filter(p => Number(p.resource_id) === Number(resourceId))
      .map(p => Number(p.id));

    const allSelected = resourcePermissions.every(id => selectedPermissions.includes(id));

    if (allSelected) {
      setSelectedPermissions(prev => prev.filter(id => !resourcePermissions.includes(id)));
    } else {
      setSelectedPermissions(prev => [...new Set([...prev, ...resourcePermissions])]);
    }
  };

  const handleSubmit = async () => {
    if (!canAssignPermissions) {
      return;
    }

    try {
      setLoading(true);
      await roleAPI.assignPermissions(role.id, { permission_ids: selectedPermissions });
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to assign permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getResourcePermissions = (resourceId) => {
    return permissions.filter(p => Number(p.resource_id) === Number(resourceId));
  };

  const isResourceFullySelected = (resourceId) => {
    const resourcePermissions = getResourcePermissions(resourceId);
    return resourcePermissions.length > 0 &&
           resourcePermissions.every(p => selectedPermissions.includes(Number(p.id)));
  };

  const isResourcePartiallySelected = (resourceId) => {
    const resourcePermissions = getResourcePermissions(resourceId);
    return resourcePermissions.some(p => selectedPermissions.includes(Number(p.id))) &&
           !isResourceFullySelected(resourceId);
  };

  const filteredResources = resources
    .filter(resource => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      const label = getResourceLabel(resource.name).toLowerCase();
      const resourcePermissions = getResourcePermissions(resource.id);
      return label.includes(query) ||
             resource.name.toLowerCase().includes(query) ||
             resourcePermissions.some(p =>
               p.action.toLowerCase().includes(query) ||
               p.description?.toLowerCase().includes(query)
             );
    })
    .sort((a, b) => getResourceLabel(a.name).localeCompare(getResourceLabel(b.name)));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Assign Permissions — {role?.name}
        {!loading && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            ({selectedPermissions.length} of {permissions.length} selected)
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Box display="flex" gap={1} mb={2} alignItems="center">
              <TextField
                fullWidth
                placeholder="Search permissions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={!canAssignPermissions}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
              {canAssignPermissions && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const allIds = permissions.map(p => Number(p.id));
                    const allSelected = allIds.every(id => selectedPermissions.includes(id));
                    setSelectedPermissions(allSelected ? [] : allIds);
                  }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {permissions.every(p => selectedPermissions.includes(Number(p.id))) ? 'Deselect All' : 'Select All'}
                </Button>
              )}
            </Box>
            {!canAssignPermissions && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                You do not have permission to assign role permissions.
              </Typography>
            )}
            {filteredResources.map((resource) => {
              const resourcePermissions = getResourcePermissions(resource.id);
              if (resourcePermissions.length === 0) return null;

              return (
                <Accordion key={resource.id}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isResourceFullySelected(resource.id)}
                          indeterminate={isResourcePartiallySelected(resource.id)}
                          disabled={!canAssignPermissions}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggleResource(resource.id);
                          }}
                        />
                      }
                      label={
                        <Typography variant="subtitle1" fontWeight={600}>
                          {getResourceLabel(resource.name)}
                        </Typography>
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1} sx={{ pl: 4 }}>
                      {resourcePermissions.map((permission) => (
                        <FormControlLabel
                          key={permission.id}
                          control={
                            <Checkbox
                              checked={selectedPermissions.includes(Number(permission.id))}
                              disabled={!canAssignPermissions}
                              onChange={() => handleTogglePermission(permission.id)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {permission.action}
                              </Typography>
                              {permission.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {permission.description}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || !canAssignPermissions}>
          Save Permissions
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignPermissionsDialog;
